import { useState } from 'react';
import type { Project } from '../lib/types';

const mockProjects: Project[] = [
  {
    id: '1',
    name: 'E-commerce Platform',
    description: 'Modern online shopping platform with advanced features',
    status: 'In Progress',
    createdDate: '2024-01-15',
    teamMembers: 8,
    category: 'Web Development'
  },
  {
    id: '2',
    name: 'Mobile Banking App',
    description: 'Secure banking application for iOS and Android',
    status: 'Draft',
    createdDate: '2024-01-20',
    teamMembers: 5,
    category: 'Mobile Development'
  },
  {
    id: '3',
    name: 'CRM Dashboard',
    description: 'Customer relationship management system',
    status: 'Completed',
    createdDate: '2024-01-10',
    teamMembers: 6,
    category: 'Enterprise Software'
  }
];

export default function ProjectManagement() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const projectCounts = {
    All: mockProjects.length,
    Draft: mockProjects.filter(p => p.status === 'Draft').length,
    'In Progress': mockProjects.filter(p => p.status === 'In Progress').length,
    Completed: mockProjects.filter(p => p.status === 'Completed').length
  };

  const filteredProjects = activeFilter === 'All'
    ? mockProjects
    : mockProjects.filter(p => p.status === activeFilter);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Draft': return 'bg-gray-100 text-gray-800';
      case 'In Progress': return 'bg-blue-100 text-blue-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (selectedProject) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <button
            onClick={() => setSelectedProject(null)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Projects
          </button>

          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedProject.name}</h1>
                <p className="text-gray-600">{selectedProject.description}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedProject.status)}`}>
                {selectedProject.status}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-6 mt-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Category</p>
                <p className="font-medium text-gray-900">{selectedProject.category}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Team Members</p>
                <p className="font-medium text-gray-900">{selectedProject.teamMembers}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Created Date</p>
                <p className="font-medium text-gray-900">{new Date(selectedProject.createdDate).toLocaleDateString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200">
            <div className="border-b border-gray-200 px-6">
              <nav className="flex space-x-8">
                {['Overview', 'User Stories', 'Development', 'Documentation'].map((tab) => (
                  <button
                    key={tab}
                    className="py-4 px-1 border-b-2 border-blue-500 text-blue-600 font-medium text-sm"
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            <div className="p-6">
              <div className="text-center py-12">
                <i className="ri-folder-open-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Project Details</h3>
                <p className="text-gray-600">Detailed project information will be displayed here</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
            <p className="text-gray-600">Manage your projects and track progress</p>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
          >
            <i className="ri-add-line mr-2"></i>
            New Project
          </button>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['All', 'Draft', 'In Progress', 'Completed'].map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeFilter === filter
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {filter}
              <span
                className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeFilter === filter ? 'bg-blue-400 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {projectCounts[filter as keyof typeof projectCounts]}
              </span>
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              onClick={() => setSelectedProject(project)}
              className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className="ri-folder-line text-blue-600 text-2xl"></i>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                  {project.status}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2">{project.name}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{project.description}</p>

              <div className="flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center">
                  <i className="ri-team-line mr-1"></i>
                  <span>{project.teamMembers} members</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-calendar-line mr-1"></i>
                  <span>{new Date(project.createdDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Create New Project</h2>
            <p className="text-gray-600 mb-4">Project creation form will be here</p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
